import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Structured address resolved from a Mapbox suggestion. All fields are plain strings
 * (names), so they map directly onto the existing address DTO fields with no backend
 * change. Replaces the brittle /Country country->state->city cascade.
 */
export interface AddressSuggestion {
  label: string;       // full address, for the suggestion list + free-text fallback
  country: string;
  state: string;       // Mapbox 'region'
  city: string;        // Mapbox 'place'
  suburb: string;      // Mapbox 'locality' (or 'neighborhood')
  postalCode: string;
  longitude?: number;
  latitude?: number;
}

/**
 * Wraps the Mapbox Geocoding v6 forward (autocomplete) endpoint. This is a third-party
 * API authenticated by a token query param, so it uses HttpClient directly (NOT
 * ServiceBase) — the app JWT must not be sent to Mapbox.
 */
@Injectable({ providedIn: 'root' })
export class MapboxAddressService {
  constructor(private http: HttpClient) {}

  /** Returns [] without a network call for queries under 3 chars. */
  search(query: string, limit = 5): Observable<AddressSuggestion[]> {
    const q = (query ?? '').trim();
    if (q.length < 3) {
      return of([]);
    }
    const token = environment.mapbox.accessToken;
    const url =
      'https://api.mapbox.com/search/geocode/v6/forward' +
      `?q=${encodeURIComponent(q)}&autocomplete=true&limit=${limit}` +
      `&types=address,place,locality,neighborhood,postcode,region,country` +
      `&access_token=${token}`;
    return this.http.get<any>(url).pipe(map((res) => this.parse(res)));
  }

  /** Parse a Mapbox geocoding response into structured suggestions. */
  parse(res: any): AddressSuggestion[] {
    const features = (res && res.features) || [];
    return features.map((f: any) => this.parseFeature(f));
  }

  private parseFeature(f: any): AddressSuggestion {
    const props = (f && f.properties) || {};
    const ctx = props.context || {};
    const type = props.feature_type;
    const name: string = props.name || '';
    const ctxName = (key: string): string => (ctx[key] && ctx[key].name) || '';

    // The matched feature itself may BE the suburb/city/state/etc; fold its own name
    // into the matching slot when the context doesn't already provide it.
    const ownIf = (...types: string[]) => (types.includes(type) ? name : '');
    const coords = (f && f.geometry && f.geometry.coordinates) || [];

    return {
      label: props.full_address || props.place_formatted || name,
      country: ctxName('country') || ownIf('country'),
      state: ctxName('region') || ownIf('region'),
      city: ctxName('place') || ownIf('place'),
      suburb: ctxName('locality') || ctxName('neighborhood') || ownIf('locality', 'neighborhood'),
      postalCode: ctxName('postcode') || ownIf('postcode'),
      longitude: coords[0],
      latitude: coords[1],
    };
  }
}
